import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Block } from 'baseui/block';
import { LabelXSmall, ParagraphSmall } from 'baseui/typography';
import { useStyletron } from 'baseui';
import CodeBlock from './CodeBlock';

const FENCE_REGEX = /```(\w+)?\s*\n?([\s\S]*?)```/g;

export const splitAiContent = (content) => {
  if (!content) return [];

  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = FENCE_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        body: content.substring(lastIndex, match.index).trim(),
      });
    }

    const language = (match[1] || 'text').toLowerCase();
    const body = match[2].trim();
    const type = language === 'mermaid' ? 'mermaid' : 'code';
    segments.push({ type, language, body });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      body: content.substring(lastIndex).trim(),
    });
  }

  if (segments.length === 0) {
    return [{ type: 'text', body: content.trim() }];
  }

  return segments.filter(
    (seg) => seg.type !== 'text' || (seg.body && seg.body.length > 0)
  );
};

const looksLikeCodeBlock = (text) => {
  if (!text || text.split('\n').length < 2) return false;
  const signals =
    (text.match(/[;{}]/g) || []).length +
    (text.match(/^\s*(public|private|protected|import|class|interface|@|<\/)/gm) || [])
      .length;
  return signals >= 2;
};

const guessLanguage = (text) => {
  if (/^\s*<[\w!?]/.test(text) || /<\/\w+>/.test(text)) return 'xml';
  if (/^\s*import java\./m.test(text) || /\bpublic class\b/.test(text)) return 'java';
  if (/^\s*def \w+/m.test(text)) return 'python';
  if (/^\s*function\s|=>/.test(text)) return 'javascript';
  return 'plaintext';
};

const renderInline = (text, theme) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={`${match.index}-b`} style={{ fontWeight: 600 }}>
          {token.slice(2, -2)}
        </strong>
      );
    } else {
      parts.push(
        <code
          key={`${match.index}-c`}
          style={{
            backgroundColor: theme.colors.mono200,
            color: theme.colors.contentPrimary,
            padding: '1px 6px',
            borderRadius: theme.borders.radius200,
            fontSize: '0.88em',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
  }

  if (last < text.length) {
    parts.push(text.slice(last));
  }

  return parts.length ? parts : text;
};

const MermaidDiagram = ({ chart, className }) => {
  const containerRef = useRef(null);
  const reactId = useId();
  const diagramId = useMemo(
    () => `ai-mermaid-${reactId.replace(/:/g, '')}`,
    [reactId]
  );
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    const removeStrayMermaidNodes = () => {
      document
        .querySelectorAll(
          `[id="d${diagramId}"], [id="${diagramId}-error"], .mermaid-error`
        )
        .forEach((node) => node.remove());
    };

    const render = async () => {
      setStatus('loading');
      if (!chart?.trim()) {
        setStatus('fail');
        return;
      }

      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: 'neutral',
          securityLevel: 'loose',
          fontFamily: 'inherit',
          suppressErrorRendering: true,
        });

        if (cancelled || !containerRef.current) return;

        await mermaid.parse(chart.trim());
        const { svg } = await mermaid.render(diagramId, chart.trim());

        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svg;
        setStatus('ok');
      } catch (err) {
        console.warn('Mermaid diagram skipped (invalid syntax):', err);
        removeStrayMermaidNodes();
        setStatus('fail');
      }
    };

    render();
    return () => {
      cancelled = true;
      removeStrayMermaidNodes();
    };
  }, [chart, diagramId]);

  if (status === 'fail') {
    return null;
  }

  return (
    <Block
      className={className}
      marginTop="scale400"
      marginBottom="scale400"
      padding="scale500"
      backgroundColor="mono200"
      overrides={{
        Block: {
          style: {
            borderRadius: '8px',
            overflowX: 'auto',
            display: status === 'ok' ? 'block' : 'none',
          },
        },
      }}
    >
      <LabelXSmall
        $style={{ marginBottom: '8px', color: '#6b7280', fontWeight: 600 }}
      >
        Architecture
      </LabelXSmall>
      <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />
    </Block>
  );
};

const TextSection = ({
  body,
  variant,
  theme,
  css,
  looksLikeCodeBlock,
  guessLanguage,
}) => {
  if (!body) return null;

  if (looksLikeCodeBlock(body)) {
    return (
      <Block marginBottom="scale400">
        <CodeBlock
          code={body.replace(/^\s*\d+\s*\|\s?/gm, '')}
          language={guessLanguage(body)}
          showLineNumbers
          variant="light"
        />
      </Block>
    );
  }

  const lines = body.split('\n');
  const isMemory = variant === 'memory';

  const textAlignLeft = { textAlign: 'left', width: '100%' };

  return (
    <Block marginBottom="scale400" $style={textAlignLeft}>
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <Block key={idx} height="scale300" />;
        }

        if (/^#{1,3}\s+/.test(trimmed)) {
          const text = trimmed.replace(/^#{1,3}\s+/, '');
          if (isMemory && /^memory map/i.test(text)) {
            return null;
          }
          return (
            <LabelXSmall
              key={idx}
              $style={{
                fontWeight: 700,
                marginTop: idx > 0 ? '14px' : 0,
                marginBottom: '10px',
                color: theme.colors.contentPrimary,
                letterSpacing: '0.01em',
                textAlign: 'left',
                display: 'block',
              }}
            >
              {renderInline(text, theme)}
            </LabelXSmall>
          );
        }

        if (/^Recall hook:/i.test(trimmed)) {
          return (
            <Block
              key={idx}
              className={css({
                marginTop: theme.sizing.scale500,
                padding: theme.sizing.scale400,
                paddingLeft: theme.sizing.scale500,
                backgroundColor: '#EFF4FF',
                borderLeftWidth: '3px',
                borderLeftStyle: 'solid',
                borderLeftColor: '#276EF1',
                borderRadius: theme.borders.radius200,
                textAlign: 'left',
              })}
            >
              <ParagraphSmall
                $style={{
                  margin: 0,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  textAlign: 'left',
                  color: '#1f2937',
                }}
              >
                {renderInline(trimmed, theme)}
              </ParagraphSmall>
            </Block>
          );
        }

        if (
          /^One-liner answer:/i.test(trimmed) ||
          (trimmed.endsWith(':') &&
            trimmed.length < 48 &&
            !trimmed.includes('—') &&
            !trimmed.includes('->'))
        ) {
          return (
            <LabelXSmall
              key={idx}
              $style={{
                fontWeight: 600,
                marginTop: '14px',
                marginBottom: '8px',
                textAlign: 'left',
                display: 'block',
                color: theme.colors.contentPrimary,
              }}
            >
              {renderInline(trimmed, theme)}
            </LabelXSmall>
          );
        }

        const isBullet = /^[-*•]\s+/.test(trimmed);
        const isNumbered = /^\d+\.\s+/.test(trimmed);
        const isMemoryTerm =
          isMemory &&
          (/^\*\*[^*]+\*\*\s*[—–-]/.test(trimmed) || isBullet || isNumbered);
        const isArrow = trimmed.includes('→') || trimmed.includes('->');

        let display = trimmed
          .replace(/^[-*•]\s+/, '')
          .replace(/^\d+\.\s+/, '');

        if (isMemoryTerm) {
          return (
            <Block
              key={idx}
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              marginBottom="scale300"
              $style={{ gap: '10px', textAlign: 'left' }}
            >
              <ParagraphSmall
                $style={{
                  margin: 0,
                  flexShrink: 0,
                  lineHeight: 1.55,
                  color: theme.colors.contentSecondary,
                  width: '12px',
                }}
              >
                •
              </ParagraphSmall>
              <ParagraphSmall
                $style={{
                  margin: 0,
                  flex: 1,
                  fontWeight: isArrow ? 500 : 400,
                  lineHeight: 1.55,
                  textAlign: 'left',
                  color: theme.colors.contentPrimary,
                }}
              >
                {renderInline(display, theme)}
              </ParagraphSmall>
            </Block>
          );
        }

        return (
          <ParagraphSmall
            key={idx}
            $style={{
              marginTop: 0,
              marginBottom: isMemory ? '8px' : '8px',
              fontWeight: isArrow ? 500 : 300,
              lineHeight: 1.55,
              textAlign: 'left',
              color: theme.colors.contentPrimary,
            }}
          >
            {renderInline(display, theme)}
          </ParagraphSmall>
        );
      })}
    </Block>
  );
};

const AiResponseRenderer = ({ content, variant = 'explain' }) => {
  const [css, theme] = useStyletron();
  const segments = useMemo(() => splitAiContent(content), [content]);

  const diagramPreClass = css({
    marginTop: theme.sizing.scale400,
    marginBottom: theme.sizing.scale400,
    padding: theme.sizing.scale500,
    backgroundColor: theme.colors.mono200,
    borderRadius: theme.borders.radius300,
    overflowX: 'auto',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '12px',
    lineHeight: 1.45,
    whiteSpace: 'pre',
    color: theme.colors.contentPrimary,
  });

  const mermaidWrapClass = css({
    ':global(svg)': {
      maxWidth: '100%',
      height: 'auto',
    },
  });

  return (
    <Block
      className="ai-response-content"
      paddingRight="scale200"
      paddingLeft="scale100"
      $style={{ textAlign: 'left', width: '100%' }}
    >
      {segments.map((segment, idx) => {
        if (segment.type === 'mermaid') {
          return (
            <MermaidDiagram
              key={idx}
              chart={segment.body}
              className={mermaidWrapClass}
            />
          );
        }

        if (segment.type === 'code') {
          const lang = segment.language;
          const isDiagramText =
            lang === 'text' &&
            /[┌┐└┘│─▶→]/.test(segment.body);

          if (isDiagramText) {
            return (
              <Block key={idx} marginBottom="scale400">
                <LabelXSmall
                  $style={{
                    marginBottom: '6px',
                    fontWeight: 600,
                    color: theme.colors.contentSecondary,
                  }}
                >
                  Diagram
                </LabelXSmall>
                <pre className={diagramPreClass}>{segment.body}</pre>
              </Block>
            );
          }

          return (
            <Block key={idx} marginTop="scale300" marginBottom="scale400">
              <CodeBlock
                code={segment.body}
                language={lang === 'text' ? 'plaintext' : lang}
                showLineNumbers={segment.body.split('\n').length > 2}
                variant="light"
              />
            </Block>
          );
        }

        return (
          <TextSection
            key={idx}
            body={segment.body}
            variant={variant}
            theme={theme}
            css={css}
            looksLikeCodeBlock={looksLikeCodeBlock}
            guessLanguage={guessLanguage}
          />
        );
      })}
    </Block>
  );
};

export default AiResponseRenderer;
